import React, { useMemo } from 'react';
import {
  faBuilding,
  faHouse,
  faPenToSquare,
  faRightFromBracket,
  faRightToBracket,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { filterNavTreeByPermissions } from 'access/routeAccess';
import { usePermissions } from 'access/usePermission';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getHomePath,
  getOfficeCheckInPath,
  getOfficeCheckOutPath,
  getSclaAppHomePath,
  getUpdateProfilePath,
} from 'utils/navigation';
import {
  resolveSclaApplication,
  type SclaApplicationStatusLabel,
} from 'utils/sclaApplicationKind';
import useSclaApplicationStatus from 'hooks/useSclaApplicationStatus';
import useUserStore from 'store/useUserStore';
import type { SidebarStatusTone } from 'components/Layout/AppShell/SidebarNavItem';

export type NavTreeNode = {
  id: string;
  title: string;
  subtitle?: string;
  statusTone?: SidebarStatusTone;
  sectionTitle?: string;
  icon: JSX.Element;
  to?: string;
  children?: NavTreeNode[];
};

const SCLA_STATUS_LINE: Record<SclaApplicationStatusLabel, string> = {
  'not started': 'Not started',
  'in progress': 'In progress',
  submitted: 'Submitted',
};

const SCLA_STATUS_TONE: Record<
  SclaApplicationStatusLabel,
  SidebarStatusTone
> = {
  'not started': 'neutral',
  'in progress': 'warning',
  submitted: 'brand',
};

function buildStudentNavTree(
  userType: string,
  status: { status: SclaApplicationStatusLabel; cohortYear: string } | null,
): NavTreeNode[] {
  const application = resolveSclaApplication(userType);
  const links: NavTreeNode[] = [
    {
      id: 'home',
      title: 'Home',
      sectionTitle: 'General',
      icon: <FontAwesomeIcon icon={faHouse} />,
      to: getHomePath(),
    },
    {
      id: 'profile',
      title: 'Profile',
      icon: <FontAwesomeIcon icon={faUser} />,
      to: getUpdateProfilePath(),
    },
    {
      id: 'txt-office',
      title: 'TXT Office',
      icon: <FontAwesomeIcon icon={faBuilding} />,
      children: [
        {
          id: 'office-check-in',
          title: 'Check In',
          subtitle: 'Arrive at the TXT Office',
          icon: <FontAwesomeIcon icon={faRightToBracket} />,
          to: getOfficeCheckInPath(),
        },
        {
          id: 'office-check-out',
          title: 'Check Out',
          subtitle: 'Sign out before you leave',
          icon: <FontAwesomeIcon icon={faRightFromBracket} />,
          to: getOfficeCheckOutPath(),
        },
      ],
    },
    // --- WIP features (restore when ready) ---
    // Courses (faGraduationCap, getTxtTrainingCoursesPath)
    // {
    //   id: 'txt-training',
    //   title: 'Courses',
    //   icon: <FontAwesomeIcon icon={faGraduationCap} />,
    //   to: getTxtTrainingCoursesPath(),
    // },
    // SCLA — My Team (dashboard) is open to all students (eligible gated
    // server-side). Mobile Application stays hidden unless the user has any
    // *-app-admin:read grant (team-derived or otherwise; see routeAccess
    // anyOfReadPermissions).
    // (faPenToSquare, faUsers, faGauge, faMobileScreen,
    //  getSclaTeamRosterPath, getSclaMobileApplicationsPath)
    // {
    //   id: 'scla',
    //   title: 'SCLA',
    //   icon: <FontAwesomeIcon icon={faPenToSquare} />,
    //   children: [
    //     {
    //       id: 'scla-my-team',
    //       title: 'My Team',
    //       icon: <FontAwesomeIcon icon={faUsers} />,
    //       children: [
    //         {
    //           id: 'scla-team-roster',
    //           title: 'Dashboard',
    //           subtitle: 'Your SCLA roster teammates',
    //           icon: <FontAwesomeIcon icon={faGauge} />,
    //           to: getSclaTeamRosterPath(),
    //         },
    //         {
    //           id: 'scla-mobile-applications',
    //           title: 'Mobile Application',
    //           subtitle: "Your team's capstone app users",
    //           icon: <FontAwesomeIcon icon={faMobileScreen} />,
    //           to: getSclaMobileApplicationsPath(),
    //         },
    //       ],
    //     },
    //   ],
    // },
    // AI Image Chat (faImage, getAiImageChatPath)
    // {
    //   id: 'ai-image-chat',
    //   title: 'AI Image Chat',
    //   icon: <FontAwesomeIcon icon={faImage} />,
    //   to: getAiImageChatPath(),
    // },
    // Forms (faFileSignature, getFormsListPath)
    // {
    //   id: 'signed-forms',
    //   title: 'Forms',
    //   icon: <FontAwesomeIcon icon={faFileSignature} />,
    //   to: getFormsListPath(),
    // },
    // --- Commented out (restore when needed) ---
    // SEALS (faPersonMilitaryPointing, faReadme, getSealsAppPath, getSealsProgramDescriptionPath)
    // TXT Labs (faLaptop, faReadme, getTxtLabsApprenticeshipAppPath, getTxtLabsApprenticeshipDescriptionPath)
    // {
    //   id: 'seals',
    //   title: 'SEALS',
    //   icon: <FontAwesomeIcon icon={faPersonMilitaryPointing} />,
    //   children: [
    //     {
    //       id: 'seals-description',
    //       title: 'Program description',
    //       icon: <FontAwesomeIcon icon={faReadme} />,
    //       to: getSealsProgramDescriptionPath(),
    //     },
    //     {
    //       id: 'seals-application',
    //       title: 'Application',
    //       icon: <FontAwesomeIcon icon={faPenToSquare} />,
    //       to: getSealsAppPath(),
    //     },
    //   ],
    // },
    // {
    //   id: 'txt-labs',
    //   title: 'TXT Labs',
    //   icon: <FontAwesomeIcon icon={faLaptop} />,
    //   children: [
    //     {
    //       id: 'txt-labs-desc',
    //       title: 'Apprenticeship',
    //       icon: <FontAwesomeIcon icon={faReadme} />,
    //       to: getTxtLabsApprenticeshipDescriptionPath(),
    //     },
    //     {
    //       id: 'txt-labs-app',
    //       title: 'Apprenticeship application',
    //       icon: <FontAwesomeIcon icon={faPenToSquare} />,
    //       to: getTxtLabsApprenticeshipAppPath(),
    //     },
    //   ],
    // },
  ];

  if (application.allowed) {
    links.splice(1, 0, {
      id: 'scla-application',
      title: 'SCLA Application',
      icon: <FontAwesomeIcon icon={faPenToSquare} />,
      to: getSclaAppHomePath(),
      subtitle: status
        ? `${status.cohortYear} · ${SCLA_STATUS_LINE[status.status]}`
        : undefined,
      statusTone: status ? SCLA_STATUS_TONE[status.status] : undefined,
    });
  }

  return links;
}

export default function useNavigationLinks() {
  const user = useUserStore(s => s.user);
  const { can } = usePermissions();
  const status = useSclaApplicationStatus(user?._id, user?.type);
  const links = useMemo(() => {
    if (!user) return [];
    return filterNavTreeByPermissions(
      buildStudentNavTree(user.type, status),
      can,
    );
  }, [user, can, status]);

  return { links };
}
